import { Injectable, inject } from "@angular/core";
import {
  ElevenLabsTranscriptStrategy,
  GoogleTranscriptStrategy,
  IVisionStrategy,
  OpenAITranscriptStrategy
} from "../models/strategies";
import { AppConfigService } from "./app.config.service";
import { UserService } from "./user.service";
import {
  AgentParam,
  EndpointStateEnum,
  ISnapshotAnalysis,
  IVisionSnapshotState,
  VisionState
} from "../models";
import {
  AIVisionEvent,
  ApplicationProviderTypeEnum,
  SNAPSHOT_TYPE_VISION,
  UserRoleEnum
} from "../core-ui.enums";
import { GenericErrorHandler } from "./error-handlers.service";
import { AuviousRtcService } from "./rtc.service";
import { ApplicationService } from "./application.service";
import { IEndpoint } from "@auvious/rtc";
import { ISnapshotRecord, SnapshotService } from "./snapshot.service";
import { BehaviorSubject, filter, firstValueFrom, map, Subject } from "rxjs";
import { ISnapshot } from "@auvious/snapshot";
import { blobToBase64, debug, debugError } from "./utils";
import { HttpClient } from "@angular/common/http";
import { ConferenceService } from "./conference.service";

/** How often the snapshots are listed while an analysis is pending. */
export const VISION_SYNC_PENDING_MS = 5_000;

/**
 * How long a vision snapshot that has no analysis yet is still listed for:
 * the agent who took it records the analysis only once the image is loaded
 * and sent to the ai. An rtc that does not keep analyses never shows one,
 * so this also bounds the listing for it.
 */
export const VISION_ANALYSIS_GRACE_MS = 60_000;

/**
 * How far along a vision state is. A state only ever moves forward: a
 * listed snapshot is older than a live event that came while the request
 * was out, and must not undo it. Both outcomes are final.
 */
const VISION_STATE_PROGRESS: Record<VisionState, number> = {
  snapshotRequested: 0,
  snapshotReady: 1,
  snapshotError: 1,
  analysisRequested: 2,
  analysisReady: 3,
  analysisError: 3
};

const FINAL_PROGRESS = 3;

/** The vision state a snapshot's recorded analysis stands for. */
const ANALYSIS_STATES: Record<ISnapshotAnalysis["state"], VisionState> = {
  PENDING: "analysisRequested",
  COMPLETED: "analysisReady",
  FAILED: "analysisError"
};

@Injectable()
export class VisionService {
  private config = inject(AppConfigService);
  private user = inject(UserService);
  private rtc = inject(AuviousRtcService);
  private logger = inject(GenericErrorHandler);
  private application = inject(ApplicationService);
  private snapshotService = inject(SnapshotService);
  private conference = inject(ConferenceService);
  private http = inject(HttpClient);

  private impl: IVisionStrategy;

  private snapshotState: Map<
    string,
    Pick<IVisionSnapshotState, "result" | "state">
  > = new Map();
  private visionRequestState: Map<string, string> = new Map(); // vision request id : snapshot id

  private _imageStateChange = new Subject<IVisionSnapshotState>();
  /** Every state change, this page's own and those listed from rtc. */
  public imageStateChange$ = this._imageStateChange.asObservable();

  private _ownImageStateChange = new Subject<IVisionSnapshotState>();
  /**
   * The state changes of what this page did (a snapshot it took, its
   * recognition and its events), not those listed from rtc: a reload
   * listing earlier results, or another agent's, is not news to open on.
   */
  public ownImageStateChange$ = this._ownImageStateChange.asObservable();

  private _imageChange = new Subject<ISnapshot>();
  public imageChange$ = this._imageChange.asObservable();

  private _imagesAvailable = new Subject<ISnapshot[]>();
  public imagesAvailable$ = this._imagesAvailable.asObservable();

  private _resultChange = new BehaviorSubject<{
    snapshotId: string;
    result: string;
  }>(undefined);
  public resultChange$ = this._resultChange.asObservable();

  /**
   * Bumped on `reset`, so an answer for the call that was left is dropped.
   */
  private call = 0;
  /** In a call (own endpoint joined) with the feature on. */
  private joined = false;
  /** A list request is out; the next one is scheduled when it answers. */
  private syncInFlight = false;
  /** Asked to list again while a request was out: do so right after it. */
  private syncAgain = false;
  private syncTimer: ReturnType<typeof setTimeout> | undefined;
  /** Every snapshot id a list answer had, of any type. */
  private known = new Set<string>();
  /** Vision snapshot id -> its analysis in the last list answer, if any. */
  private listed = new Map<string, ISnapshotAnalysis | undefined>();
  /** Vision snapshot id -> when it was first listed without an analysis. */
  private listedWithoutAnalysisAt = new Map<string, number>();

  constructor() {
    this.config.configChanged$.subscribe((c) => {
      let provider;
      if (this.user.isAdmin) {
        provider = c.serviceParameters?.transcriptProvider;
      } else if (this.user.isAgent) {
        if (c.agentParameters?.[AgentParam.TRANSCRIPT_PROVIDER]) {
          provider = c.agentParameters[AgentParam.TRANSCRIPT_PROVIDER];
        }
      }
      if (provider) this.providerChanged(provider);
    });

    this.snapshotService.snapshotStateChanged$
      .pipe(filter((s) => s.type === SNAPSHOT_TYPE_VISION))
      .subscribe((snapshot) => {
        if (snapshot.signedUrl) {
          // we have the actual snapshot
          this.setSnapshotState(snapshot.id, "snapshotReady");
          this.loadImageAndRecognize(snapshot);
        } else {
          // this is the temp snapshot until we get the new one
          this.setSnapshotState(snapshot.id, "snapshotRequested");
          // this.requestState.set(snapshot., )
        }
        this._imageChange.next(snapshot);
      });

    // Joining (and rejoining: a reload, a reconnect) reads the analyses rtc
    // keeps on the conference's snapshots: the results of snapshots another
    // agent took, and of this page's own before a reload.
    this.conference.endpointJoined$
      .pipe(
        filter(
          (endpoint) =>
            !!endpoint?.endpoint &&
            endpoint.endpoint === this.rtc.myself?.endpoint
        )
      )
      .subscribe(() => this.startSync());

    // The conference emits our own endpoint as Left when it is destroyed:
    // the call is over, so is its state (this service outlives it).
    this.conference.endpointStateChanged$
      .pipe(
        filter(
          (change) =>
            change?.state === EndpointStateEnum.Left &&
            change.endpoint === this.rtc.myself?.endpoint
        )
      )
      .subscribe(() => this.reset());

    // A snapshot this page has not listed yet (another agent's, or the
    // room loading the interaction's): list again to learn its analysis.
    this.snapshotService.snapshotsAvailable$
      .pipe(
        filter((all) =>
          (all || []).some(
            (s) =>
              !!s?.id && !this.known.has(s.id) && !this.snapshotState.has(s.id)
          )
        )
      )
      .subscribe(() => this.syncNow());

    this.snapshotService.snapshotsAvailable$
      .pipe(map((a) => a.filter((s) => s.type === SNAPSHOT_TYPE_VISION)))
      .subscribe((snapshots) => {
        this._imagesAvailable.next(snapshots.reverse());
      });
  }

  /**
   * Forgets the snapshots of the call being left and stops listing them.
   * Without it the next call would show the previous call's results.
   */
  public reset(): void {
    this.call++;
    this.joined = false;
    this.stopTimer();
    this.syncInFlight = false;
    this.syncAgain = false;
    this.snapshotState.clear();
    this.visionRequestState.clear();
    this.known.clear();
    this.listed.clear();
    this.listedWithoutAnalysisAt.clear();
  }

  /** Agent-side users with AI vision on; nobody else reads analyses. */
  public get isEnabled(): boolean {
    return (
      !!this.user.isAgent &&
      !!this.config.agentParamEnabled(AgentParam.AI_VISION_ENABLED)
    );
  }

  /**
   * Moves a snapshot to `state`, unless it is already as far or further
   * along (see `VISION_STATE_PROGRESS`). Answers whether it moved.
   *
   * One exception: the analysis stored on the snapshot is authoritative, so
   * a listed result replaces an error. The ai stores the result first and
   * only then notifies; when that notification fails it sends a failure
   * event instead, and the result it stored is still the answer.
   */
  private setSnapshotState(
    id: string,
    state: VisionState,
    result?: string,
    listed = false
  ): boolean {
    const current = this.snapshotState.get(id)?.state;
    const storedResultOverError =
      listed && state === "analysisReady" && current === "analysisError";

    if (
      current &&
      VISION_STATE_PROGRESS[state] <= VISION_STATE_PROGRESS[current] &&
      !storedResultOverError
    ) {
      return false;
    }

    this.snapshotState.set(id, { state, result });
    this._imageStateChange.next({ id, state, result });
    if (!listed) {
      this._ownImageStateChange.next({ id, state, result });
    }
    return true;
  }

  private progressOf(id: string): number {
    const state = this.snapshotState.get(id)?.state;
    return state ? VISION_STATE_PROGRESS[state] : -1;
  }

  /**
   * Whether a listed analysis is still to come: one rtc shows as pending,
   * or a fresh snapshot without one yet (see `VISION_ANALYSIS_GRACE_MS`).
   * A snapshot this page already has an outcome for is not waited on —
   * except an error that rtc still shows as pending: the stored analysis
   * can yet turn out a result (see `setSnapshotState`).
   */
  private get hasPending(): boolean {
    const now = Date.now();

    for (const [id, analysis] of this.listed) {
      const awaitingStored =
        analysis?.state === "PENDING" &&
        this.snapshotState.get(id)?.state === "analysisError";

      if (this.progressOf(id) >= FINAL_PROGRESS && !awaitingStored) continue;

      if (analysis?.state === "PENDING") return true;

      const since = this.listedWithoutAnalysisAt.get(id);
      if (
        !analysis &&
        since !== undefined &&
        now - since < VISION_ANALYSIS_GRACE_MS
      ) {
        return true;
      }
    }
    return false;
  }

  private startSync(): void {
    if (!this.isEnabled) return;

    this.joined = true;
    this.syncNow();
  }

  private stopTimer(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = undefined;
    }
  }

  /** Lists now, or right after the request that is out: never two at once. */
  private syncNow(): void {
    if (!this.joined) return;

    this.stopTimer();

    if (this.syncInFlight) {
      this.syncAgain = true;
      return;
    }

    void this.sync();
  }

  /**
   * The next list: right away when asked while the last was out, in
   * `VISION_SYNC_PENDING_MS` while an analysis is pending; otherwise none
   * until something new comes (a join, a snapshot, a recognition).
   */
  private scheduleSync(): void {
    if (!this.joined) return;

    const again = this.syncAgain;
    this.syncAgain = false;

    if (!again && !this.hasPending) return;

    this.syncTimer = setTimeout(
      () => {
        this.syncTimer = undefined;
        void this.sync();
      },
      again ? 0 : VISION_SYNC_PENDING_MS
    );
  }

  /**
   * Lists the interaction's snapshots and merges in their analyses. A
   * failure (the network, a 403, an rtc that is not reachable) is logged
   * and retried on the next tick; it is never shown to the agent.
   */
  private async sync(): Promise<void> {
    if (!this.joined) return;

    if (!this.isEnabled) {
      this.joined = false;
      this.stopTimer();
      return;
    }

    const interactionId = this.snapshotService.interactionId;
    const call = this.call;

    this.syncInFlight = true;

    let failed = false;

    try {
      if (!interactionId) {
        // the room has not loaded the interaction's snapshots yet
        failed = true;
        return;
      }

      const records = await this.snapshotService.listByInteractionId(
        interactionId,
        // polled: a failure is retried on the next tick, not here
        { retries: 1 }
      );

      // the call was left while the request was out: the answer is stale
      if (call !== this.call) return;

      this.merge(records);
    } catch (ex) {
      failed = true;
      debug("vision: could not list the interaction's snapshots", ex);
    } finally {
      if (call === this.call) {
        this.syncInFlight = false;

        if (failed && !this.syncAgain) {
          this.syncTimer = setTimeout(() => {
            this.syncTimer = undefined;
            void this.sync();
          }, VISION_SYNC_PENDING_MS);
        } else {
          this.scheduleSync();
        }
      }
    }
  }

  /**
   * Takes the vision snapshots' recorded analyses in: PENDING, COMPLETED and
   * FAILED stand for the states a live event would set, and like those, are
   * applied only when they take the snapshot further — save a COMPLETED one,
   * which also replaces an error (see `setSnapshotState`). The task id lets a
   * late MQTT event of this user's own request match after a reload.
   */
  private merge(records: ISnapshotRecord[]): void {
    const now = Date.now();

    for (const record of records || []) {
      if (record?.id) this.known.add(record.id);

      if (!record?.id || record.type !== SNAPSHOT_TYPE_VISION) continue;

      const analysis = record.analysis || undefined;
      const state = analysis ? ANALYSIS_STATES[analysis.state] : undefined;

      this.listed.set(record.id, state ? analysis : undefined);

      if (!state) {
        if (!this.listedWithoutAnalysisAt.has(record.id)) {
          this.listedWithoutAnalysisAt.set(record.id, now);
        }
        continue;
      }

      if (analysis.taskId && !this.visionRequestState.has(analysis.taskId)) {
        this.visionRequestState.set(analysis.taskId, record.id);
      }

      this.setSnapshotState(
        record.id,
        state,
        state === "analysisReady" ? analysis.result : undefined,
        true
      );
    }
  }

  public getStateForSnapshot(id: string): IVisionSnapshotState {
    return { id, ...this.snapshotState.get(id) };
  }

  private async loadImageAndRecognize(snapshot: ISnapshot) {
    try {
      const url = await firstValueFrom(snapshot.signedUrl);
      const imageBase64 = await this.convertImageToBase64(url);
      const response = await this.recognize(imageBase64, snapshot.id);

      this.visionRequestState.set(response.id, snapshot.id);
      this.setSnapshotState(snapshot.id, "analysisRequested");
      // the ai now records the analysis on the snapshot: follow it there
      // too, in case its event does not reach this page
      this.syncNow();
    } catch (ex) {
      debugError(ex);
    }
  }

  public supported(endpoint: IEndpoint): boolean {
    const roles = endpoint?.metadata?.roles || [];
    // const capabilities = endpoint?.metadata?.capabilities || [];
    const hasCustomerRole = roles.includes(UserRoleEnum.customer);
    return hasCustomerRole;
  }

  public providerChanged(provider: ApplicationProviderTypeEnum) {
    switch (provider) {
      case ApplicationProviderTypeEnum.OPEN_AI:
        this.impl = new OpenAITranscriptStrategy(
          this.logger,
          this.rtc,
          this.application
        );
        break;
      case ApplicationProviderTypeEnum.GOOGLE_AI_STUDIO:
      case ApplicationProviderTypeEnum.GOOGLE_CLOUD:
        this.impl = new GoogleTranscriptStrategy(
          this.logger,
          this.rtc,
          this.application
        );
    }
  }

  private async convertImageToBase64(imageUrl: string): Promise<string> {
    try {
      const blob = await firstValueFrom(
        this.http.get(imageUrl, { responseType: "blob" })
      );
      const base64 = await blobToBase64(blob);
      return base64;
    } catch (ex) {
      debugError(ex);
      throw ex;
    }
  }

  public propagateEvent(event: {
    id: string;
    result: string;
    timestamp: string;
    type: AIVisionEvent;
  }) {
    let snapshotId;
    switch (event.type) {
      case "AIRecognitionCreatedEvent":
        snapshotId = this.visionRequestState.get(event.id);
        if (snapshotId) {
          this.setSnapshotState(snapshotId, "analysisReady", event.result);
        }
        break;
      case "AIRecognitionFailedEvent":
        snapshotId = this.visionRequestState.get(event.id);
        if (snapshotId) {
          this.setSnapshotState(snapshotId, "analysisError");
        }
    }
  }

  /**
   * Starts a recognition of a snapshot's image. With `snapshotId` the ai
   * records its progress and result on that snapshot, for other agents and
   * for after the call.
   */
  recognize(
    imageBase64: string,
    snapshotId?: string,
    model?: string
  ): Promise<{ id: string }> {
    return this.impl.recognize(
      this.config.agentParam(AgentParam.AI_VISION_PROMPT),
      imageBase64,
      model,
      snapshotId
    );
  }
}
